import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'

import useMutation from '@hooks/useGQLMutation'
import { ExportRosterDocument } from '@frontend/graphql/mutations/exportRoster.generated'
import { useAlertsStore } from '@stores/alertsStore'
import { toast } from '@stores/toastStore'
import { FormDialog } from '@components/molecules/FormDialog'
import { RadioGroupField } from '@components/molecules/RadioGroupField'
import { scopedTranslation } from '@utils/I18n'

const t = scopedTranslation('components.roster_export_modal')

type ExportFormat = 'pdf' | 'csv'
type FormData = { format: ExportFormat }

const schema = yup
  .object({
    format: yup.mixed<ExportFormat>().oneOf(['pdf', 'csv']).required(),
  })
  .required()

type ExportableEvent = {
  id: string
  name: string
}

type RosterExportModalProps = {
  open: boolean
  setOpen: (open: boolean) => void
  event: ExportableEvent
}

export function RosterExportModal({ open, setOpen, event }: RosterExportModalProps) {
  const { setAlerts, clearAlerts } = useAlertsStore(({ setAlerts, clearAlerts }) => ({ setAlerts, clearAlerts }))
  const alertsPath = `rosterExport:${event.id}`

  const { handleSubmit, setValue, watch } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: { format: 'pdf' },
  })
  const format = watch('format')

  const exportMutation = useMutation(ExportRosterDocument)

  const showError = (title: string, description?: string) =>
    setAlerts({ alertsPath: alertsPath, alerts: [{ type: 'error', details: { title, description } }] })

  const onSubmit = ({ format: chosenFormat }: FormData) => {
    clearAlerts(alertsPath)

    exportMutation.mutate(
      { input: { id: event.id, format: chosenFormat } },
      {
        onSuccess: (response) => {
          if (response?.exportRoster?.errors?.[0]?.code === 'no_published_shifts') {
            showError(t('alerts.no_published_shifts.title'), t('alerts.no_published_shifts.body'))
            return
          }
          if (!response?.exportRoster?.success) {
            showError(t('alerts.error.title'), t('alerts.error.body'))
            return
          }
          setOpen(false)
          toast.success(t('alerts.success.title'), { description: t('alerts.success.body') })
        },
        onError: () => showError(t('alerts.error.title'), t('alerts.error.body')),
      },
    )
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      clearAlerts(alertsPath)
      setOpen(false)
    }
  }

  return (
    <FormDialog.Root open={open} onOpenChange={handleOpenChange} size="xl" testId="roster-export-modal">
      <FormDialog.Title alertsPath={alertsPath}>{t('title', { event_name: event.name })}</FormDialog.Title>
      <FormDialog.Form onSubmit={handleSubmit(onSubmit)}>
        <FormDialog.Body>
          <p className="tw:text-sm tw:text-gray-700">{t('description')}</p>
          <RadioGroupField<ExportFormat>
            label={t('form.format.label')}
            value={format}
            onValueChange={(value) => setValue('format', value)}
            options={[
              { value: 'pdf', label: t('form.format.pdf') },
              { value: 'csv', label: t('form.format.csv') },
            ]}
          />
        </FormDialog.Body>
        <FormDialog.Footer submitText={t('form.submit')} isLoading={exportMutation.isLoading} />
      </FormDialog.Form>
    </FormDialog.Root>
  )
}
